import * as React from 'react';
import {
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Typography,
  styled,
  FormHelperText,
} from '@mui/material';
import { Check, Clear, Visibility, VisibilityOff } from '@mui/icons-material';
import { FieldInputProps } from 'formik/dist/types';
import PasswordStrength from './PasswordStrength';

const CustomFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
}));

const CustomListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0),
}));

interface PropTypes {
  passwordTouched: boolean | undefined;
  passwordError: string | undefined;
  fieldProps: FieldInputProps<unknown>;
  requirements?: boolean;
  label?: string;
  showStrength?: boolean;
  setIsValidPwd?: React.Dispatch<React.SetStateAction<boolean>>;
}

// eslint-disable-next-line complexity
const PasswordFormField: React.FC<PropTypes> = ({ passwordTouched, passwordError, fieldProps, setIsValidPwd, requirements, label, showStrength = false }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordRequirements, setPasswordRequirements] = React.useState({
    length: false,
    lowercase: false,
    uppercase: false,
    numbers: false,
    fade: false,
  });

  React.useEffect(() => {
    if (typeof fieldProps.value === 'string') {
      const length = fieldProps.value.length >= 6;
      const lowercase = /[a-z]/.test(fieldProps.value);
      const uppercase = /[A-Z]/.test(fieldProps.value);
      const numbers = /[0-9]/.test(fieldProps.value);
      setPasswordRequirements({
        length,
        lowercase,
        uppercase,
        numbers,
        fade: fieldProps.value !== '',
      });
    }
  }, [fieldProps]);

  return (
    <CustomFormControl variant="outlined">
      <InputLabel htmlFor="outlined-adornment-password" style={{ color: '#546E7A' }}>
        {label ?? 'Password'}
      </InputLabel>
      <OutlinedInput
        error={!!passwordTouched && !!passwordError}
        id="outlined-adornment-password"
        type={showPassword ? 'text' : 'password'}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
              }}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        label={label ?? 'Password'}
        {...fieldProps}
      />
      {!!passwordTouched && !!passwordError && (
        <FormHelperText error id="accountId-error">
          This field is required
        </FormHelperText>
      )}
      {!showStrength && passwordRequirements.fade && requirements && (
        <Fade in timeout={500}>
          <Grid
            item
            sm={12}
            style={{
              marginTop: 20,
              padding: 10,
              border: '1px solid silver',
            }}
          >
            <>
              <Typography>Your password must contain:</Typography>
              <List>
                <CustomListItem>
                  <ListItemIcon>{passwordRequirements.length ? <Check style={{ color: 'green' }} /> : <Clear style={{ color: 'red' }} />}</ListItemIcon>
                  <ListItemText
                    style={{
                      color: passwordRequirements.length ? 'green' : 'red',
                    }}
                    primary="At least 6 character"
                  />
                </CustomListItem>

                <CustomListItem>
                  <ListItemIcon>{passwordRequirements.numbers ? <Check style={{ color: 'green' }} /> : <Clear style={{ color: 'red' }} />}</ListItemIcon>
                  <ListItemText
                    style={{
                      color: passwordRequirements.numbers ? 'green' : 'red',
                    }}
                    primary="At least 1 number (0-9)"
                  />
                </CustomListItem>

                <CustomListItem>
                  <ListItemIcon>{passwordRequirements.lowercase ? <Check style={{ color: 'green' }} /> : <Clear style={{ color: 'red' }} />}</ListItemIcon>
                  <ListItemText
                    style={{
                      color: passwordRequirements.lowercase ? 'green' : 'red',
                    }}
                    primary="At least 1 lowercase letter (az)"
                  />
                </CustomListItem>

                <CustomListItem>
                  <ListItemIcon>{passwordRequirements.uppercase ? <Check style={{ color: 'green' }} /> : <Clear style={{ color: 'red' }} />}</ListItemIcon>
                  <ListItemText
                    style={{
                      color: passwordRequirements.uppercase ? 'green' : 'red',
                    }}
                    primary="At least 1 uppercase letter (A-Z)"
                  />
                </CustomListItem>
              </List>
            </>
          </Grid>
        </Fade>
      )}

      {showStrength && requirements && (
        <>
          <PasswordStrength length={12} setIsValidPwd={setIsValidPwd} value={typeof fieldProps.value === 'string' ? fieldProps.value : ''} />

          <FormHelperText>Password must be at least 6 characters long, including one uppercase, one lowercase and a number</FormHelperText>
        </>
      )}
    </CustomFormControl>
  );
};

export default PasswordFormField;
